import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { Media, Medias } from '@core/interfaces/media.interface';
import { MultimediasService } from '@core/services/programs/multimedias.service';
import { StepsService } from '@core/services/programs/steps.service';
import { Steps } from '@core/interfaces/Steps.interface';
import { FilterService } from '@core/services/filters/filter.service';
import {
  WHATSAPP_FLOW_LANGUAGES,
  WHATSAPP_FLOW_OPTIONS,
  WhatsappFlowOption,
} from '@core/constants/whatsapp-flows';

@Component({
  selector: 'app-dialog-media',
  templateUrl: './dialog-media.component.html',
  styleUrls: ['./dialog-media.component.scss'],
})
export class DialogMediaComponent implements OnInit, OnDestroy {
  public archivos: File | null = null;
  public pageIndex: number = 1;
  public pageSize: number = 50;
  public totalItems?: number;
  displayedColumns: string[] = ['name', 'fileUrl', 'type', 'action'];
  public listMultimedias: Media[] = [];
  dataSource = new MatTableDataSource<Media>(this.listMultimedias);
  @Output() pageChange: EventEmitter<any> = new EventEmitter<any>();
  /** Suscripción a los cambios de filtros. */
  private filterSubscription: Subscription | undefined;
  public files: File[] = [];
  public searchValue: string = '';
  public multimediaCtrl = new FormControl();

  public folder: string = '';
  public listSelect: any[] = [
    { value: 'enlace' },
    { value: 'multimedia' },
    { value: 'cargar' },
  ];
  public step!: FormGroup;
  public fileData: { name: string; base: any } = { name: '', base: '' };
  public idMultimedia: string = '';

  /** Opciones de WhatsApp Flows disponibles (deben existir en FlowRouter del chatbot). */
  public flowOptions: WhatsappFlowOption[] = WHATSAPP_FLOW_OPTIONS;
  public flowLanguages: string[] = WHATSAPP_FLOW_LANGUAGES;

  constructor(
    private fb: FormBuilder,
    public _stepsService: StepsService,
    private _mediasService: MultimediasService,
    private filterService: FilterService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<DialogMediaComponent>,
  ) {
    console.log('data =>', data);
  }

  ngOnInit(): void {
    this.getListMultimedias();

    this.step = this.fb.group({
      numberStep: [''],
      interaction: ['Informativo', Validators.required],
      question: [''],
      description: [''],
      typeMultimedia: [''],
      media: [''],
      name: [''],
      title: [''],
      fileUrl: [''],
      files: [''],
      flowEnabled: [false],
      flowTokenFlow: [''],
      flowNameFlow: [''],
      flowCode: ['es_MX'],
    });
    if (this.data && this.data.step !== undefined) {
      this.step.patchValue({
        numberStep: this.data.step.numberStep,
        interaction: this.data.step.interaction,
        question: this.data.step.question,
        description: this.data.step.description,
        media: this.data.step.media?._id ? this.data.step.media?._id : '',
        flowEnabled: !!this.data.step.flows?.token_flow,
        flowTokenFlow: this.data.step.flows?.token_flow || '',
        flowNameFlow: this.data.step.flows?.name_flow || '',
        flowCode: this.data.step.flows?.code || 'es_MX',
      });
    }

    this.step.get('files')?.valueChanges.subscribe((files) => {
      if (files && files.length > 0) {
        this.step.get('fileUrl')?.disable();
      } else {
        this.step.get('fileUrl')?.enable();
      }
    });

    // Suscripción a los cambios de filtros
    this.filterSubscription = this.filterService.filter$.subscribe(
      (filters) => {
        this.searchValue = filters.searchValue;
        // Obtener la lista de programas paginada con los filtros aplicados
        this.getListMultimedias(this.searchValue);
      },
    );
  }

  ngOnDestroy(): void {
    this.step.reset();
    this.multimediaCtrl.reset();
    this.listMultimedias = [];
    this.files = [];
    // Destruir la suscripción
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
  }

  getListMultimedias(searchValue: string = '') {
    if (searchValue) {
      this.pageIndex = 1;
    }
    this._mediasService
      .getListMedia(this.pageIndex, this.pageSize, searchValue)
      .subscribe(
        (resp: any) => {
          this.listMultimedias = resp.results.data;
          this.totalItems = resp.results.pagination.total;
          this.dataSource.data = this.listMultimedias;
          this.dataSource = new MatTableDataSource(this.listMultimedias);
        },
        (error) => {
          console.error(error);
        },
      );
  }

  /**
   * Traduce los campos planos del formulario (flowEnabled/flowTokenFlow/flowNameFlow/flowCode)
   * al shape que espera el modelo Steps en backend: { name_flow, token_flow, code } | null.
   */
  buildFlowsValue(): { name_flow: string; token_flow: string; code: string } | null {
    const { flowEnabled, flowTokenFlow, flowNameFlow, flowCode } = this.step.value;
    if (!flowEnabled || !flowTokenFlow || !flowNameFlow) {
      return null;
    }
    return {
      name_flow: flowNameFlow,
      token_flow: flowTokenFlow,
      code: flowCode || 'es_MX',
    };
  }

  editStepById(id: string) {
    const payload = { ...this.step.value, flows: this.buildFlowsValue() };
    this._stepsService.updatedStep(id, payload).subscribe(
      (resp: Steps) => {
        if (resp.ok) {
          this.handleMediaUpload();
        }
      },
      (err) => {
        console.log(err);
      },
    );
  }

  async eventStep() {
    if (this.data && this.data.step !== undefined) {
      await this.editStepById(this.data.step._id);
      this.dialogRef.close();
    } else {
      this.dialogRef.close({ ...this.step.value, flows: this.buildFlowsValue() });
    }
  }

  registerMedia() {
    let newMedia = {
      name: this.step.value.title,
      fileUrl: this.step.value.fileUrl,
    };

    this._mediasService.guardarLinkVideo(newMedia).subscribe(
      (res: any) => {
        this.handleMediaUpload();
        this.step.value.media = res;
        this.step.value.fileUrl = '';
        this.step.value.title = '';
      },
      (err) => {
        console.error('Error al registrar la media:', err);
        let errorMessage = 'Se ha presentado un error al registrar la media';
        if (err && err.error && err.error.message) {
          errorMessage = err.error.message;
        }
        Swal.fire('Registro de medios', errorMessage, 'error');
      },
    );
  }

  /**
   * Maneja el cambio de página del paginador.
   * @param event Evento de cambio de página.
   */
  onPageChange(event: any) {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.pageChange.emit({
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    });
    this.getListMultimedias(this.searchValue);
  }

  cargarArchivo() {
    const formData = new FormData();
    formData.append('carpeta', this.step.value.title || this.archivos?.name);
    if (this.archivos instanceof File) {
      formData.append('file', this.archivos);
    }

    this._mediasService.cargarMedia(formData).subscribe(
      (res: any) => {
        Swal.fire({
          icon: 'success',
          title: 'Se ha registrado la media correctamente',
          iconHtml: '<i class="ri-check-line" style="color: green;"></i>',
          customClass: {
            popup: 'swal-custom-popup',
            title: 'swal-custom-title',
            htmlContainer: 'swal-custom-content',
          },
          showConfirmButton: false,
          timer: 3000,
          backdrop: false,
          toast: true,
          position: 'top-end',
          background: '#ffffff',
          color: '#1d2c4d',
          padding: '1rem',
          width: '20rem',
          showClass: {
            popup: 'animate__animated animate__fadeInDown',
          },
          hideClass: {
            popup: 'animate__animated animate__fadeOutUp',
          },
        });

        if (res.ok) {
          this.step.value.media = res.data.id;
        }
      },
      (err) => {
        console.error('Error al cargar la media:', err);
        let errorMessage = 'Se ha presentado un error al cargar la media';
        if (err && err.error && err.error.message) {
          errorMessage = err.error.message;
        }
        Swal.fire('Carga de medios', errorMessage, 'error');
      },
    );
  }

  selectMedia(event: any) {
    this.step.value.media = event.value;
  }

  onSelect(event: any) {
    if (event.addedFiles.length > 0) {
      const file = event.addedFiles[0];
      this.files.push(file);
      this.archivos = file;
      this.step.get('files')?.setValue(this.files);
    }
  }

  onRemove(event: any) {
    this.files.splice(this.files.indexOf(event), 1);
    this.step.get('files')?.setValue(this.files);
    if (this.files.length === 0) {
      this.step.get('fileUrl')?.enable();
    }
  }

  checkFileUrl() {
    if (this.step.get('fileUrl')?.value) {
      this.files = [];
      this.step.get('files')?.reset();
    }
  }

  obtenerCarpetaPorFormato(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension) {
      if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
        return 'img';
      } else if (['doc', 'docx', 'xlsx', 'pdf'].includes(extension)) {
        return 'documents';
      } else if (['mp3'].includes(extension)) {
        return 'audio';
      }
    }
    return 'otros';
  }

  saveMedia() {
    if (this.files.length > 0) {
      this.cargarArchivo();
    } else if (this.step.get('fileUrl')?.value) {
      this.registerMedia();
    }
  }

  async handleMediaUpload(): Promise<void> {
    Swal.fire({
      icon: 'success',
      text: 'Se actualizó el paso correctamente',
      showConfirmButton: false,
      timer: 3000,
      backdrop: false,
      toast: true,
      position: 'top-end',
      background: '#ffffff',
      color: '#1d2c4d',
      padding: '1rem',
      width: '25rem',
      showClass: {
        popup: 'animate__animated animate__fadeInDown',
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOutUp',
      },
    });
  }

  close() {
    this.dialogRef.close();
  }
}
